// cache-bust:20260526-p2-quiz-audit
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PARFUM CENTRAL - FRAGRANCE QUIZ LOGIC
 * assets/quiz-fragrance.js
 *
 * Handles all quiz interactions, state management, product recommendations,
 * and Klaviyo integration for "The Curation" fragrance quiz.
 * ═══════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────
  // CONFIGURATION
  // ─────────────────────────────────────────────────────────────────────────

  const CONFIG = {
    // Animation timings (match CSS)
    transitionDuration: 400,
    staggerDelay: 100,

    // Progress display
    romanNumerals: ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'],

    // Total questions
    totalQuestions: 7, // 7 screens: 6 questions + email (Intent through Email)

    // Quiz submission worker URL (Cloudflare Worker)
    // Deploy the worker from quiz-submission-worker/worker.js and paste URL here
    submissionWorkerUrl: 'https://api.parfumcentral.com',
    storefrontApiKey: 'c8190abe180155ef22c00c8ba7761b9d',

    // Product recommendations per category
    productsPerCategory: {
      primary: 3,    // "Your Signature Match" - products 1-3
      seasonal: 3,   // "Seasonal Companions" - products 4-6
      discovery: 3   // "For the Adventurous" - products 7-9
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // QUIZ STATE
  // ─────────────────────────────────────────────────────────────────────────

  const STORAGE_KEY = 'parfum_quiz_state';

  const state = {
    currentStep: 1,       // Start at question 1 (1-indexed)
    answers: {
      intent: null,       // Q1: 'for-me' | 'for-someone'
      atmosphere: null,   // Q2: dimly-lit-bar | sunlit-room | quiet-study | serene-garden
      time: null,         // Q3: early-morning | late-afternoon | after-sunset | deep-night
      place: null,        // Q4: library | coastline | garden | temple | hotel-bar | workshop
      impression: null,   // Q5: power | warmth | nostalgia | mystery (What should stay in the room)
      avoid: [],          // Q6: Multi-select array - what to avoid
      email: '',          // Q7
      firstName: '',      // Q7
      smsConsent: false,  // Q7
      phone: ''           // Q7 (optional, shown when smsConsent is true)
    },
    cart: {
      items: [],
      total: 0,
      discountCode: null
    },
    recommendations: null
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SESSION STORAGE - Persist quiz progress
  // ─────────────────────────────────────────────────────────────────────────

  function saveProgress() {
    try {
      const dataToSave = {
        currentStep: state.currentStep,
        answers: state.answers,
        timestamp: Date.now()
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (e) {
      console.warn('[Quiz] Could not save progress:', e);
    }
  }

  function loadProgress() {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        // Only restore if saved within last 30 minutes
        if (Date.now() - data.timestamp < 30 * 60 * 1000) {
          state.currentStep = data.currentStep || 0;
          state.answers = { ...state.answers, ...data.answers };
          return true;
        } else {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (e) {
      console.warn('[Quiz] Could not load progress:', e);
    }
    return false;
  }

  function clearProgress() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('[Quiz] Could not clear progress:', e);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ARCHETYPES (for results display)
  // New system: 6 archetypes with name, scene, and description
  // ─────────────────────────────────────────────────────────────────────────

  const ARCHETYPES = {
    'sovereign': {
      name: 'The Sovereign',
      scene: 'Picture a quiet throne room at dawn: everything in place, power unspoken.',
      description: 'The Sovereign represents control, structure, and permanence. Oud, ambered woods, and restrained spice were chosen to project stability rather than provoke attention. The goal isn\'t dominance through force, but authority that feels settled and unquestioned, commanding respect without raising a voice.'
    },
    'rebel': {
      name: 'The Rebel',
      scene: 'Leather jacket, late-night alley, cigarette glow cutting the dark.',
      description: 'The Rebel represents defiance and self-direction. Patchouli, bitter citrus, leather, and smoke were chosen to feel confrontational and raw, rejecting comfort and refinement. The intention is to leave an impression that\'s impossible to smooth over, no apologies, no blending in.'
    },
    'philosopher': {
      name: 'The Philosopher',
      scene: 'Candlelit study, rain against the window, pages turning slowly.',
      description: 'The Philosopher represents contemplation and depth. Incense, dry woods, and soft resins slow the senses and resist immediacy. This isn\'t about impact, but about creating space for thought to unfold, inviting reflection long after the moment passes.'
    },
    'seducer': {
      name: 'The Seducer',
      scene: 'Whispered conversation in dim light, skin brushing skin, pulse quickening.',
      description: 'The Seducer represents attraction through closeness. Musks, balsams, soft florals, and skin-like woods are used to feel intimate rather than loud. The intention is to draw someone in, not announce presence from a distance, subtle, magnetic, unforgettable up close.'
    },
    'explorer': {
      name: 'The Explorer',
      scene: 'Horizon stretching wide, wind whipping through open windows, next unknown ahead.',
      description: 'The Explorer represents curiosity and movement. Citrus, fresh spices, aromatics, and mineral notes were chosen to feel energizing and transitional, resisting anything static. The goal is momentum, always pointing forward, never settling.'
    },
    'ascetic': {
      name: 'The Ascetic',
      scene: 'Empty room, single beam of light, breath steady and deliberate.',
      description: 'The Ascetic represents discipline and clarity. Clean woods, subtle musks, light smoke, and paper-like notes remove excess to sharpen focus. The intention is restraint, stripping away distraction so what remains feels deliberate, essential, and profoundly present.'
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SCORING MAP - Maps quiz answers to archetype points
  // ─────────────────────────────────────────────────────────────────────────

  const SCORING_MAP = {
    // Q4: Place - 5/2-3 points (PRIMARY)
    place: {
      'library': { primary: { archetype: 'philosopher', pts: 5 }, secondary: { archetype: 'sovereign', pts: 2 } },
      'coastline': { primary: { archetype: 'explorer', pts: 5 }, secondary: { archetype: 'ascetic', pts: 2 } },
      'garden': { primary: { archetype: 'ascetic', pts: 4 }, secondary: { archetype: 'explorer', pts: 3 } },
      'temple': { primary: { archetype: 'philosopher', pts: 4 }, secondary: { archetype: 'ascetic', pts: 3 } },
      'hotel-bar': { primary: { archetype: 'seducer', pts: 4 }, secondary: { archetype: 'sovereign', pts: 3 } },
      'workshop': { primary: { archetype: 'rebel', pts: 5 }, secondary: { archetype: 'ascetic', pts: 2 } }
    },
    // Q5: Impression - 4/1-2 points
    impression: {
      'power': { primary: { archetype: 'sovereign', pts: 4 }, secondary: { archetype: 'rebel', pts: 1 } },
      'warmth': { primary: { archetype: 'seducer', pts: 4 }, secondary: { archetype: 'explorer', pts: 1 } },
      'nostalgia': { primary: { archetype: 'philosopher', pts: 3 }, secondary: { archetype: 'ascetic', pts: 2 } },
      'mystery': { primary: { archetype: 'rebel', pts: 4 }, secondary: { archetype: 'philosopher', pts: 1 } }
    },
    // Q2: Atmosphere - 3/1 points
    atmosphere: {
      'dimly-lit-bar': { primary: { archetype: 'rebel', pts: 3 }, secondary: { archetype: 'sovereign', pts: 1 } },
      'sunlit-room': { primary: { archetype: 'explorer', pts: 3 }, secondary: { archetype: 'ascetic', pts: 1 } },
      'quiet-study': { primary: { archetype: 'philosopher', pts: 3 }, secondary: { archetype: 'seducer', pts: 1 } },
      'serene-garden': { primary: { archetype: 'ascetic', pts: 3 }, secondary: { archetype: 'seducer', pts: 1 } }
    },
    // Q3: Time - 2/1 points
    time: {
      'early-morning': { primary: { archetype: 'ascetic', pts: 2 }, secondary: { archetype: 'philosopher', pts: 1 } },
      'late-afternoon': { primary: { archetype: 'sovereign', pts: 2 }, secondary: { archetype: 'explorer', pts: 1 } },
      'after-sunset': { primary: { archetype: 'seducer', pts: 2 }, secondary: { archetype: 'philosopher', pts: 1 } },
      'deep-night': { primary: { archetype: 'rebel', pts: 2 }, secondary: { archetype: 'sovereign', pts: 1 } }
    },
    // Q6: Avoid - PENALTIES (-2 each)
    avoid: {
      'no-sweet': { penalize: 'seducer', pts: -2 },
      'no-sharp': { penalize: 'explorer', pts: -2 },
      'no-familiar': { penalize: 'sovereign', pts: -2 },
      'no-loud': { penalize: 'rebel', pts: -2 },
      'nothing': { penalize: null, pts: 0 }
    }
  };

  const TIEBREAK_ORDER = ['sovereign', 'seducer', 'explorer', 'philosopher', 'rebel', 'ascetic'];

  // ─────────────────────────────────────────────────────────────────────────
  // ARCHETYPE PRODUCTS - Fixed product lists per archetype
  // Each archetype has 9 products: 1-3 = Signature Match, 4-6 = Seasonal, 7-9 = Adventurous
  // ─────────────────────────────────────────────────────────────────────────

  const ARCHETYPE_PRODUCTS = {
    'ascetic': [
      { name: 'Initio Musk Therapy Extrait de Parfum', handle: 'initio-musk-therapy-edp', notes: 'Bergamot, Mandarin \u00b7 White Magnolia, Blackcurrant \u00b7 White Sandalwood, White Musk, Pink Musk' },
      { name: 'Initio Paragon', handle: 'initio-paragon', notes: 'Bergamot, White Sage, Lavender \u00b7 Palo Santo, Black Pepper \u00b7 Sandalwood, Agarwood' },
      { name: 'Tom Ford Neroli Portofino', handle: 'tom-ford-neroli-portofino', notes: 'Bergamot, Mandarin, Lemon, Bitter Orange, Lavender, Rosemary \u00b7 African Orange Flower, Neroli, Jasmine \u00b7 Amber, Musk, Angelica' },
      { name: 'Byredo Bal\'d Afrique', handle: 'byredo-bald-afrique', notes: 'Bergamot, Lemon, Neroli, African Marigold, Buchu \u00b7 Violet, Jasmine Petals, Cyclamen \u00b7 Black Amber, Musk, Vetiver, Moroccan Cedarwood' },
      { name: 'Memo Paris Madurai EDP', handle: 'memo-paris-madurai-edp', notes: 'Lemon, Bergamot, Turmeric, Clary Sage, Peach \u00b7 Jasmine Sambac, Tuberose, Jasmine, Tagetes \u00b7 Sandalwood, Suede' },
      { name: 'Initio High Frequency EDP', handle: 'initio-high-frequency-edp', notes: 'Almond, Hedione \u00b7 Magnolia, Heliotrope, Jasmine \u00b7 Vanilla, Sandalwood' },
      { name: 'Carolina Herrera Cedar Chic EDP', handle: 'carolina-herrera-cedar-chic-edp', notes: 'Aldehydes, Bergamot \u00b7 Orange Blossom \u00b7 Cedarwood, White Musk' },
      { name: 'Clive Christian Kinam Parfum - Dubai Exclusive', handle: 'clive-christian-kinam-parfum-dubai-exclusive', notes: 'Saffron, Davana, Nutmeg \u00b7 Metallic Notes, Osmanthus, Rose de Mai \u00b7 Kinam Oud, Sandalwood, Amber' },
    ],
    'explorer': [
      { name: 'Boadicea Hanuman EDP', handle: 'boadicea-hanuman-edp', notes: 'Lemon, Orange Blossom, Bergamot, Blackcurrant \u00b7 Osmanthus, Neroli, Magnolia, Cyclamen \u00b7 Amber, Cedarwood, Musk, Sandalwood, Vetiver' },
      { name: 'Creed Delphinus', handle: 'creed-delphinus', notes: 'Bergamot, Lemon, Grapefruit \u00b7 Aromatic Herbs, Marine Accord \u00b7 Ambergris, Woods, Musk' },
      { name: 'Creed Silver Mountain Water', handle: 'creed-silver-mountain-water', notes: 'Bergamot, Blackcurrant, Galbanum, Orange \u00b7 Tea, Ozonic Accord \u00b7 Musk, Sandalwood' },
      { name: 'Parfums de Marly Greenley', handle: 'parfums-de-marly-greenley', notes: 'Bergamot, Mandarin, Green Apple \u00b7 Petitgrain \u00b7 Ambrox, Amberwood, Cedarwood' },
      { name: 'Parfums de Marly Perseus', handle: 'parfums-de-marly-perseus', notes: 'Bergamot, Grapefruit \u00b7 Geranium, Blackcurrant Bud \u00b7 Vetiver, Amberwood, Grey Amber' },
      { name: 'Xerjoff 40 Knots', handle: 'xerjoff-40-knots', notes: 'Sea Notes, Green Notes \u00b7 Salt, Cedarwood \u00b7 Amber, Musk, Woody Notes' },
      { name: 'Xerjoff Levar Del Sole EDP', handle: 'xerjoff-levar-del-sole-edp', notes: 'Rose, Lemon, Orange, Bergamot, Apple, Green Notes \u00b7 Cardamom, Violet, Jasmine, Lily \u00b7 Patchouli, Musk, Oakmoss, Tonka Bean, Caramel, Benzoin' },
      { name: 'Clive Christian Crab Apple Blossom', handle: 'clive-christian-crab-apple-blossom-edp', notes: 'Sea Notes, Bergamot, Apple Blossom \u00b7 Mojito, Rhubarb \u00b7 Driftwood, Sandalwood' },
      { name: 'Creed Wild Vetiver EDP', handle: 'creed-wild-vetiver-edp', notes: 'Bergamot, Pink Pepper, Timur \u00b7 Rose, Blackcurrant, Geranium \u00b7 Vetiver, Amberwood, Cedarwood' },
    ],
    'seducer': [
      { name: 'Kilian Angels Share EDP', handle: 'kilian-angels-share-edp', notes: 'Cognac \u00b7 Cinnamon, Tonka Bean, Oak \u00b7 Praline, Vanilla, Sandalwood' },
      { name: 'Parfums de Marly Herod', handle: 'parfums-de-marly-herod', notes: 'Cinnamon, Pepper \u00b7 Tobacco Leaf, Incense, Cistus, Osmanthus \u00b7 Vanilla, Musk, Cedarwood' },
      { name: 'Parfums de Marly Delina EDP', handle: 'parfums-de-marly-delina-edp', notes: 'Bergamot, Lychee, Rhubarb \u00b7 Damascena Rose, Nutmeg \u00b7 Cashmeran, Musk, Vetiver' },
      { name: 'Initio High Frequency EDP', handle: 'initio-high-frequency-edp', notes: 'Almond, Hedione \u00b7 Magnolia, Heliotrope, Jasmine \u00b7 Vanilla, Sandalwood' },
      { name: 'Initio Psychedelic Love EDP', handle: 'initio-psychedelic-love-edp', notes: 'Bergamot \u00b7 Rose, Heliotrope, Hedione \u00b7 Myrrh, Vanilla, Patchouli' },
      { name: 'Nishane Hundred Silent Ways Extrait de Parfum', handle: 'nishane-hundred-silent-ways-edp', notes: 'Tuberose, Mandarin, Peach \u00b7 Gardenia, Jasmine, Orris \u00b7 Vanilla, Sandalwood, Vetiver' },
      { name: 'Initio Absolute Aphrodisiac EDP', handle: 'initio-absolute-aphrodisiac-edp', notes: 'White Flowers \u00b7 Amber \u00b7 Vanilla, Musk, Castoreum' },
      { name: 'Ex Nihilo Scarlet Sands EDP - Dubai Exclusive', handle: 'ex-nihilo-scarlet-sands-edp-dubai-exclusive', notes: 'Raspberry, Mandarin \u00b7 Orange Blossom, Jasmine, Freesia \u00b7 Amber, Akigalawood, Vanilla' },
      { name: 'Roja Elixir Pour Femme EDP', handle: 'roja-elixir-pour-femme-edp', notes: 'Bergamot \u00b7 Raspberry, Rose de Mai, Peach, Violet, Heliotrope, Ylang-Ylang, Jasmine, Geranium, Lily-of-the-Valley \u00b7 Musk, Vanilla, Cashmere Wood, Orris, Sandalwood, Cedar, Cinnamon' },
    ],
    'philosopher': [
      { name: 'Amouage Purpose EDP', handle: 'amouage-purpose-edp', notes: 'Bergamot, Pink Pepper, Frankincense, Pimento Berry \u00b7 Rose, Sand Vetiver, Sandalwood, Papyrus \u00b7 Saffron, Suede, Mystikal, Akigalawood' },
      { name: 'Fragrance Du Bois Cannabis Blue', handle: 'fragrance-du-bois-cannibis-blue', notes: 'Cardamom, Elemi, Eucalyptus, Sichuan Pepper, Grapefruit \u00b7 Cannabis Accord, Nutmeg, Sage \u00b7 Oud, Guaiac Wood, Patchouli, Cedarwood' },
      { name: 'Francesca Bianchi The Code Emotion Extrait de Parfume', handle: 'francesca-bianchi-the-code-emotion-extrait-de-parfume', notes: 'Bergamot, Pink Pepper \u00b7 Exotic Fruits Accord, Lavender, Geranium \u00b7 Sandalwood, Cedarwood, Oakmoss, Vetiver' },
      { name: 'Memo Paris Cappadocia EDP', handle: 'memo-paris-cappadocia-edp', notes: 'Saffron \u00b7 Jasmine, Orris Root, Rose \u00b7 Sandalwood, Frankincense, Myrrh, Benzoin, Vanilla' },
      { name: 'Frederic Malle Hope EDP', handle: 'frederic-malle-hope-edp', notes: 'Juniper, Pink Pepper, Cypress \u00b7 Incense, Vetiver \u00b7 Oud, Amber' },
      { name: 'Sospiro Accento Viola EDP', handle: 'sospiro-accento-viola-edp', notes: 'Pineapple, Hyacinth \u00b7 Jasmine, Iris, Pink Pepper \u00b7 Musk, Amber, Vetiver, Patchouli, Vanilla' },
    ],
    'sovereign': [
      { name: 'Xerjoff Oud Stars Alexandria II EDP', handle: 'xerjoff-oud-stars-alexandria-ii-edp', notes: 'Apple, Cinnamon, Lavender, Rosewood \u00b7 Cedarwood, Lily of the Valley, Bulgarian Rose \u00b7 Amber, Sandalwood, Musk, Oud, Vanilla' },
      { name: 'The Spirit of Dubai Fakhama EDP', handle: 'the-spirit-of-dubai-fakhama-edp', notes: 'Turkish Rose, Bulgarian Rose, Damask Rose, Bergamot, Fruity Notes, Oud, Sweet Orange, Spicy Notes, Elemi, Cardamom, Coffee, Dried Fruits, Candied Fruit \u00b7 Sandalwood, Cypriol, Benzoin, Vanilla, Patchouli, Vetiver, Coriander, Ginger, Rose, Saffron \u00b7 Amber, Castoreum, Civet, Moss, Caramelised Sugar, Incense, Leather, Oakmoss' },
      { name: 'Initio Oud for Greatness NEO EDP', handle: 'initio-oud-for-greatness-neo-edp', notes: 'Lavender, Saffron, Nutmeg \u00b7 Oud, Patchouli \u00b7 Amber, Musk, Sandalwood' },
      { name: 'Creed Absolu Aventus', handle: 'creed-absolu-aventus', notes: 'Bergamot, Ginger, Grapefruit \u00b7 Cardamom, Cinnamon, Pineapple \u00b7 Labdanum, Vetiver, Patchouli' },
      { name: 'Maison Crivelli Oud Stallion Extrait de Parfum', handle: 'maison-crivelli-oud-stallion-edp', notes: 'Bergamot, Saffron \u00b7 Rose, Patchouli \u00b7 Oud, Leather, Amber, Musk' },
      { name: 'Creed Aventus', handle: 'creed-aventus', notes: 'Bergamot, Lemon, Blackcurrant \u00b7 Pineapple, Pink Pepper, Jasmine \u00b7 Birch, Patchouli, Musk' },
      { name: 'Clive Christian 1872 Masculin', handle: 'clive-christian-1872-masculin', notes: 'Citrus, Petitgrain, Peach, Lavender, Rosemary, Black Pepper, Nutmeg \u00b7 Clary Sage, Jasmine, Neroli, Musk \u00b7 Cedarwood, Sandalwood, Freesia, Frankincense' },
      { name: 'Clive Christian XXI Blonde Amber', handle: 'clive-christian-xxi-blonde-amber', notes: 'Bitter Orange \u00b7 Blonde Tobacco, Tuberose, Jasmine, Dried Fruits \u00b7 Tonka Bean, Amber' },
      { name: 'Penhaligon\'s Halfeti Amber EDP - Dubai Exclusive', handle: 'penhaligons-halfeti-amber-edp-dubai-exclusive', notes: 'Saffron, Suede, Amber' },
    ],
    'rebel': [
      { name: 'Parfums de Marly Pegasus Exclusif', handle: 'parfums-de-marly-pegasus-exclusif', notes: 'Pink Pepper, Cardamom, Almond \u00b7 Lavender, Orange Blossom, Geranium \u00b7 Vanilla, Ambrox, Oud, Guaiac Wood' },
      { name: 'Initio Side Effect EDP', handle: 'initio-carnal-side-effect-edp', notes: 'Rum, Cinnamon, Saffron \u00b7 Tobacco, Vanilla \u00b7 Sandalwood, Hedione' },
      { name: 'Tom Ford Tuscan Leather', handle: 'tom-ford-tuscan-leather', notes: 'Raspberry, Saffron, Thyme \u00b7 Olibanum, Jasmine \u00b7 Leather, Suede, Amberwood' },
      { name: 'Tom Ford Black Orchid', handle: 'tom-ford-black-orchid', notes: 'Black Truffle, Bergamot, Bitter Orange, Ylang-Ylang, Blackcurrant \u00b7 Black Orchid, Black Plum, Rum, Lotus Wood \u00b7 Patchouli, Incense, Vetiver, Vanilla, Balsam, Sandalwood' },
      { name: 'Matiere Premiere Falcon Leather EDP', handle: 'matiere-premiere-falcon-leather-edp', notes: 'Saffron, Labdanum \u00b7 Birch Tar, Benzoin \u00b7 Leather' },
      { name: 'Sospiro Erba Leather EDP', handle: 'sospiro-erba-leather-edp', notes: 'Passionfruit, Bergamot, Lemon \u00b7 Leather, Jasmine, Sandalwood \u00b7 Leather, Amber, Vetiver' },
      { name: 'Frederic Malle French Lover EDP', handle: 'frederic-malle-french-lover-edp', notes: 'Pimiento, Angelica, Juniper \u00b7 Galbanum, Orris, Cedar \u00b7 Patchouli, Vetiver, Frankincense' },
      { name: 'Sospiro Opera Grande EDP', handle: 'sospiro-opera-grande-edp', notes: 'Fruity Notes, Turkish Rose \u00b7 Ylang-Ylang, Nutmeg, Leather, Ambergris \u00b7 Vanilla, Musk, Patchouli, Vetiver, Virginian Cedar' },
      { name: 'Maison Crivelli Tobacco Carnaval Extrait de Parfum', handle: 'maison-crivelli-tobacco-carnaval-extrait-de-parfum', notes: 'Tobacco Leaf, Rum, Fruity Accord, Liqueur Accord' },
    ]
  };

  // ─────────────────────────────────────────────────────────────────────────
  // DOM ELEMENTS
  // ─────────────────────────────────────────────────────────────────────────

  let elements = {};

  function cacheElements() {
    elements = {
      container: document.getElementById('fragrance-quiz'),
      progressFill: document.querySelector('[data-progress-fill]'),
      progressCurrent: document.querySelector('[data-progress-current]'),
      questions: document.querySelectorAll('.quiz-question'),
      loading: document.querySelector('[data-state="loading"]'),
      results: document.querySelector('[data-state="results"]'),
      resultsSummary: document.querySelector('.quiz-results-summary'),
      cartCount: document.querySelector('[data-cart-count]'),
      cartTotal: document.querySelector('[data-cart-total]'),
      profileName: document.querySelector('[data-profile-name]'),
      profileDescription: document.querySelector('[data-profile-description]'),
      productTemplate: document.getElementById('quiz-product-template'),
      discountTracker: document.querySelector('.quiz-discount-tracker'),
      discountMessage: document.querySelector('[data-discount-message]'),
      discountStatus: document.querySelector('[data-discount-status]'),
      archetypeLink: document.querySelector('.quiz-archetype-link:not(.quiz-archetype-link--compact)')
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // INITIALIZATION
  // ─────────────────────────────────────────────────────────────────────────

  function init() {
    cacheElements();

    if (!elements.container) {
      console.warn('Quiz container not found');
      return;
    }

    // Gateway question is now built into Liquid as Question 1 (Intent)
    // No need to insert dynamically

    // Bind event listeners
    bindOptionSelections();
    bindMultiSelectOptions();
    bindNavigationButtons();
    bindEmailForm();
    bindQuantitySelectors();
    bindResultsActions();
    bindScrollFadeHints();

    // Try to restore saved progress
    const hasProgress = loadProgress();

    if (hasProgress && state.currentStep >= 1) {
      // Restore selections for previous answers
      restoreSelections();
      showQuestion(state.currentStep);
    } else {
      // Show first question (Question 1: Intent)
      showQuestion(1);
    }

    updateProgress();

  }

  function restoreSelections() {
    // Questions are now 1-indexed (1-8)
    // Q1: Intent, Q2: Atmosphere, Q3: Time, Q4: Place, Q5: Impression, Q6: Avoid (multi), Q7: Email

    // Restore single-select answers (map question number to answer key)
    const answerMap = {
      1: 'intent',
      2: 'atmosphere',
      3: 'time',
      4: 'place',
      5: 'impression'
    };

    Object.entries(answerMap).forEach(([qNum, answerKey]) => {
      const value = state.answers[answerKey];
      if (value) {
        const option = document.querySelector(`[data-question="${qNum}"] [data-value="${value}"]`);
        if (option) {
          option.setAttribute('data-selected', 'true');
          const radio = option.querySelector('input[type="radio"]');
          if (radio) radio.checked = true;
        }
      }
    });

    // Restore multi-select (avoid) - Q6
    if (state.answers.avoid && state.answers.avoid.length > 0) {
      state.answers.avoid.forEach(value => {
        const option = document.querySelector(`[data-question="6"] [data-value="${value}"]`);
        if (option) {
          option.setAttribute('data-selected', 'true');
          const checkbox = option.querySelector('input[type="checkbox"]');
          if (checkbox) checkbox.checked = true;
        }
      });
    }

    // Restore SMS consent + phone (Q8)
    const smsCheckbox = document.getElementById('quiz-sms-consent');
    const phoneInput = document.getElementById('quiz-phone');
    if (smsCheckbox && state.answers.smsConsent) {
      smsCheckbox.checked = true;
    }
    if (phoneInput && state.answers.phone) {
      phoneInput.value = state.answers.phone;
    }

  }

  // ─────────────────────────────────────────────────────────────────────────
  // HELPER FUNCTIONS
  // ─────────────────────────────────────────────────────────────────────────

  // Load asset URLs from Liquid-generated config
  let assetConfig = null;
  function loadAssetConfig() {
    if (assetConfig) return assetConfig;
    const configEl = document.getElementById('quiz-asset-config');
    if (configEl) {
      try {
        assetConfig = JSON.parse(configEl.textContent);
      } catch (e) {
        console.warn('[Quiz] Could not parse asset config:', e);
        assetConfig = {};
      }
    } else {
      assetConfig = {};
    }
    return assetConfig;
  }

  function getAssetUrl(filename) {
    // Try to get from Liquid-generated config
    const config = loadAssetConfig();

    // Map filenames to config keys (using atmosphere images as placeholders)
    const keyMap = {
      'quiz-gateway-self.jpg': 'gateway-self',
      'quiz-gateway-him.jpg': 'gateway-him',
      'quiz-gateway-her.jpg': 'gateway-her',
      'quiz-gateway-unisex.jpg': 'gateway-unisex',
      // Also support direct atmosphere image references
      'quiz-atmosphere-lounge.jpg': 'gateway-self',
      'quiz-atmosphere-library.jpg': 'gateway-him',
      'quiz-atmosphere-sunlight.jpg': 'gateway-her',
      'quiz-atmosphere-quiet.jpg': 'gateway-unisex'
    };

    const key = keyMap[filename];
    if (key && config[key]) {
      return config[key];
    }

    // Fallback for development
    return `/assets/${filename}`;
  }

  function formatCurrency(amount) {
    var formatted = Number(amount).toFixed(2);
    return '<span class="money">Dhs.' + formatted + '</span>';
  }

  // ─────────────────────────────────────────────────────────────────────────
  // NAVIGATION & PROGRESS
  // ─────────────────────────────────────────────────────────────────────────

  function showQuestion(questionNum) {
    // Use data-question attribute to find the right element (questions are 1-8)
    elements.questions.forEach((q) => {
      const qNum = parseInt(q.getAttribute('data-question'), 10);
      const isTarget = qNum === questionNum;

      if (isTarget) {
        q.setAttribute('data-visible', 'true');
        q.style.display = 'block';
        // Trigger animation
        q.classList.remove('quiz-fade-in');
        void q.offsetWidth; // Force reflow
        q.classList.add('quiz-fade-in');
        // Re-check scroll fade now that the step is visible
        setTimeout(() => {
          const opts = q.querySelector('.quiz-options');
          if (opts) opts.dispatchEvent(new Event('scroll'));
        }, 150);
      } else {
        q.setAttribute('data-visible', 'false');
        q.style.display = 'none';
      }
    });

    state.currentStep = questionNum;
    updateProgress();
    updateNavigationButtons();

    // Scroll to top of quiz
    elements.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function updateProgress() {
    // Questions are 1-8, so progress = (current - 1) / (total - 1) * 100
    const progress = (state.currentStep - 1) / (CONFIG.totalQuestions - 1) * 100;

    if (elements.progressFill) {
      elements.progressFill.style.width = `${progress}%`;
    }

    // Update progress dots - activate the current step dot
    const dots = document.querySelectorAll('.quiz-progress__dot');
    dots.forEach((dot, index) => {
      const stepNum = index + 1; // dots are 0-indexed but represent steps 1-8
      if (stepNum < state.currentStep) {
        dot.classList.add('quiz-progress__dot--completed');
        dot.classList.remove('quiz-progress__dot--active');
      } else if (stepNum === state.currentStep) {
        dot.classList.add('quiz-progress__dot--active');
        dot.classList.remove('quiz-progress__dot--completed');
      } else {
        dot.classList.remove('quiz-progress__dot--active', 'quiz-progress__dot--completed');
      }
    });

    if (elements.progressCurrent) {
      // Show roman numeral for current step (1-indexed, so romanNumerals[step-1])
      const display = CONFIG.romanNumerals[state.currentStep - 1] || state.currentStep;
      elements.progressCurrent.textContent = display;
    }
  }

  function updateNavigationButtons() {
    // Find current question by data-question attribute (1-8)
    const currentQuestion = document.querySelector(`[data-question="${state.currentStep}"]`);
    if (!currentQuestion) return;

    const backBtn = currentQuestion.querySelector('[data-action="back"]');
    const nextBtn = currentQuestion.querySelector('[data-action="next"]');
    const submitBtn = currentQuestion.querySelector('[data-action="submit"]');

    // Back button - disabled on first question (Q1)
    if (backBtn) {
      backBtn.disabled = state.currentStep === 1;
    }

    // Next button - check if current question is answered
    if (nextBtn) {
      const isAnswered = isCurrentQuestionAnswered();
      nextBtn.disabled = !isAnswered;
    }
  }

  function isCurrentQuestionAnswered() {
    const step = state.currentStep;

    // Questions are now 1-indexed (1-8)
    switch (step) {
      case 1: return state.answers.intent !== null;      // Q1: Intent
      case 2: return state.answers.atmosphere !== null;  // Q2: Atmosphere
      case 3: return state.answers.time !== null;        // Q3: Time
      case 4: return state.answers.place !== null;       // Q4: Place
      case 5: return state.answers.impression !== null;  // Q5: Impression (What should stay)
      case 6: return true;                               // Q6: Avoid (multi-select, can be empty)
      case 7: return state.answers.email !== '';         // Q7: Email
      default: return false;
    }
  }

  function goToNextQuestion() {
    // Questions are 1-8, so max is totalQuestions (8)
    if (state.currentStep < CONFIG.totalQuestions) {
      showQuestion(state.currentStep + 1);
    }
  }

  function goToPreviousQuestion() {
    // Minimum is question 1
    if (state.currentStep > 1) {
      showQuestion(state.currentStep - 1);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // OPTION SELECTION (Single Select)
  // ─────────────────────────────────────────────────────────────────────────

  function bindOptionSelections() {
    document.addEventListener('click', (e) => {
      const option = e.target.closest('.quiz-option');
      if (!option) return;

      const value = option.getAttribute('data-value');
      const questionEl = option.closest('.quiz-question');
      const questionIndex = parseInt(questionEl.getAttribute('data-question'), 10);

      // Deselect siblings
      const siblings = questionEl.querySelectorAll('.quiz-option');
      siblings.forEach(sib => sib.setAttribute('data-selected', 'false'));

      // Select this option
      option.setAttribute('data-selected', 'true');

      // Update state based on question
      updateAnswerForQuestion(questionIndex, value);

      // Save progress to session storage
      saveProgress();

      // Enable next button
      updateNavigationButtons();
    });
  }

  function updateAnswerForQuestion(questionIndex, value) {
    // Questions are now 1-indexed (1-8)
    // Q6 (avoid), Q8 (email) have separate handlers
    switch (questionIndex) {
      case 1:
        state.answers.intent = value;
        break;
      case 2:
        state.answers.atmosphere = value;
        break;
      case 3:
        state.answers.time = value;
        break;
      case 4:
        state.answers.place = value;
        break;
      case 5:
        state.answers.impression = value;
        break;
      // Q7 (approach) removed
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MULTI-SELECT OPTIONS (Question 5)
  // ─────────────────────────────────────────────────────────────────────────

  function bindMultiSelectOptions() {
    document.addEventListener('click', (e) => {
      const multiOption = e.target.closest('.quiz-multi-option');
      if (!multiOption) return;


      // Prevent default label behavior to avoid double-toggle
      e.preventDefault();

      const value = multiOption.getAttribute('data-value');
      const isSelected = multiOption.getAttribute('data-selected') === 'true';

      // Handle "surprise me" - clears all others
      if (value === 'nothing') {
        const allOptions = document.querySelectorAll('.quiz-multi-option');
        allOptions.forEach(opt => {
          opt.setAttribute('data-selected', opt.getAttribute('data-value') === 'nothing' ? 'true' : 'false');
        });
        state.answers.avoid = ['nothing'];
      } else {
        // Toggle this option
        multiOption.setAttribute('data-selected', (!isSelected).toString());

        // Remove "surprise me" if selecting something else
        const surpriseOption = document.querySelector('.quiz-multi-option[data-value="nothing"]');
        if (surpriseOption) {
          surpriseOption.setAttribute('data-selected', 'false');
        }

        // Update state array
        if (!isSelected) {
          if (!state.answers.avoid.includes(value)) {
            state.answers.avoid.push(value);
          }
        } else {
          state.answers.avoid = state.answers.avoid.filter(v => v !== value);
        }

        // Remove nothing from array
        state.answers.avoid = state.answers.avoid.filter(v => v !== 'nothing');
      }

      // Update selected count display
      updateMultiSelectCount();

      // Save progress to session storage
      saveProgress();

    });
  }

  function updateMultiSelectCount() {
    const countEl = document.querySelector('.quiz-multi-selected-count');
    const countValue = document.querySelector('[data-count]');

    if (countEl && countValue) {
      const count = state.answers.avoid.filter(v => v !== 'nothing').length;
      countValue.textContent = count;
      countEl.setAttribute('data-visible', (count > 0).toString());
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MOBILE SCROLL FADE HINTS
  // Hides the right-edge gradient when user scrolls to the end
  // ─────────────────────────────────────────────────────────────────────────

  function bindScrollFadeHints() {
    if (window.innerWidth > 600) return; // Mobile only

    document.querySelectorAll('.quiz-options').forEach(container => {
      if (container.classList.contains('quiz-options--six') ||
        container.classList.contains('quiz-options--text-only')) return;

      function checkScroll() {
        const atStart = container.scrollLeft <= 10;
        const atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
        container.classList.toggle('scrolled-start', atStart);
        container.classList.toggle('scrolled-end', atEnd);
      }

      container.addEventListener('scroll', checkScroll, { passive: true });
      // Set initial state when step becomes visible
      checkScroll();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // NAVIGATION BUTTONS
  // ─────────────────────────────────────────────────────────────────────────

  function bindNavigationButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;

      const action = btn.getAttribute('data-action');

      switch (action) {
        case 'next':
          if (!btn.disabled) {
            goToNextQuestion();
          }
          break;
        case 'back':
          goToPreviousQuestion();
          break;
        case 'retake':
          retakeQuiz();
          break;
        case 'share':
          shareResults();
          break;
        case 'checkout':
          proceedToCheckout();
          break;
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // EMAIL FORM (Question 9)
  // ─────────────────────────────────────────────────────────────────────────

  function bindEmailForm() {
    const form = document.getElementById('quiz-email-form');
    if (!form) return;

    // Real-time email validation
    const emailInput = form.querySelector('#quiz-email');
    if (emailInput) {
      emailInput.addEventListener('input', (e) => {
        state.answers.email = e.target.value;
        updateNavigationButtons();
        saveProgress();
      });
    }

    // First name input
    const nameInput = form.querySelector('#quiz-name');
    if (nameInput) {
      nameInput.addEventListener('input', (e) => {
        state.answers.firstName = e.target.value;
        saveProgress();
      });
    }

    // Phone input (always visible)
    const phoneInput = document.getElementById('quiz-phone');
    // SMS consent checkbox
    const smsCheckbox = form.querySelector('input[name="sms_consent"]');
    if (smsCheckbox) {
      smsCheckbox.addEventListener('change', () => {
        state.answers.smsConsent = smsCheckbox.checked;
        saveProgress();
      });
    }
    if (phoneInput) {
      phoneInput.addEventListener('input', (e) => {
        state.answers.phone = e.target.value;
        saveProgress();
      });
    }

    // Form submission
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      submitQuiz();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // QUIZ SUBMISSION
  // ─────────────────────────────────────────────────────────────────────────

  async function submitQuiz() {

    // Validate email
    if (!state.answers.email || !isValidEmail(state.answers.email)) {
      alert('Please enter a valid email address');
      return;
    }

    // Tier 3 EMQ persist (2026-05-08): cache email + phone in localStorage
    // and KV via /t/identity so every subsequent page event in this and
    // future browser sessions carries the identity forward to Meta CAPI.
    // funnel-tracking.liquid attaches pcCaptureIdentity to window; safe to
    // skip when the helper is missing (older theme deploys).
    try {
      if (window.pcCaptureIdentity) {
        window.pcCaptureIdentity(state.answers.email, state.answers.phone || null, 'quiz');
      }
    } catch (e) { /* never break submit */ }

    // Show loading state
    showLoading();

    try {
      // Send data to Klaviyo
      await sendToKlaviyo();

      // Generate recommendations
      state.recommendations = await generateRecommendations();

      // Save to Shopify metaobjects (non-blocking)
      sendToShopify().catch(err => console.warn('[Quiz] Shopify save failed:', err));

      // Clear session storage after successful submission
      clearProgress();

      // Show results
      await showResults();

    } catch (error) {
      console.error('[Quiz] Submission error:', error);
      // Still show results even if Klaviyo fails
      state.recommendations = await generateRecommendations();
      // Try to save to Shopify anyway
      sendToShopify().catch(err => console.warn('[Quiz] Shopify save failed:', err));
      clearProgress();
      await showResults();
    }
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function showLoading() {
    // Hide current question
    elements.questions.forEach(q => {
      q.style.display = 'none';
      q.setAttribute('data-visible', 'false');
    });

    // Show loading
    if (elements.loading) {
      elements.loading.style.display = 'flex';
      elements.loading.setAttribute('data-visible', 'true');
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // KLAVIYO INTEGRATION
  // ─────────────────────────────────────────────────────────────────────────

  async function sendToKlaviyo() {
    // Prepare profile properties
    const profileData = {
      email: state.answers.email,
      first_name: state.answers.firstName || undefined,

      // Quiz answers as custom properties
      quiz_completed: true,
      quiz_completed_at: new Date().toISOString(),
      quiz_intent: state.answers.intent,
      quiz_atmosphere: state.answers.atmosphere,
      quiz_time: state.answers.time,
      quiz_place: state.answers.place,
      quiz_impression: state.answers.impression,  // Q5: What should stay in the room
      quiz_avoid: state.answers.avoid.join(', '), // Q6: What to avoid
      quiz_sms_consent: state.answers.smsConsent,
      quiz_phone: state.answers.phone || '',

      // Computed properties - use new archetype system
      quiz_scent_profile: ARCHETYPES[calculateArchetype()]?.name || 'The Sovereign',
      quiz_gender_preference: getGenderPreference()
    };

    // Use Klaviyo's client-side API if available
    if (window._learnq) {
      window._learnq.push(['identify', profileData]);
      window._learnq.push(['track', 'Quiz Completed', {
        intent: state.answers.intent,
        scent_profile: profileData.quiz_scent_profile,
        atmosphere: state.answers.atmosphere,
        place: state.answers.place,
        impression: state.answers.impression
      }]);
    }

    // Also send to backend for list subscription (optional)
    // This would require a Shopify app or custom endpoint

    return profileData;
  }

  function getGenderPreference() {
    switch (state.answers.intent) {
      case 'self': return 'unspecified';
      case 'gift-him': return 'masculine';
      case 'gift-her': return 'feminine';
      case 'unisex': return 'unisex';
      default: return 'unspecified';
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SHOPIFY SUBMISSION (via Cloudflare Worker)
  // ─────────────────────────────────────────────────────────────────────────

  async function sendToShopify() {
    // Skip if worker URL not configured
    if (!CONFIG.submissionWorkerUrl) {
      return null;
    }

    // Build recommendations JSON for email template
    const allRecs = [];
    if (state.recommendations?.categories?.primary) {
      state.recommendations.categories.primary.forEach(r => {
        allRecs.push({
          title: r.name,
          brand: r.brand,
          price: r.priceFormatted || '',
          url: r.url || 'https://parfumcentral.com',
          image: r.image || ''
        });
      });
    }
    if (state.recommendations?.categories?.seasonal) {
      state.recommendations.categories.seasonal.forEach(r => {
        allRecs.push({
          title: r.name,
          brand: r.brand,
          price: r.priceFormatted || '',
          url: r.url || 'https://parfumcentral.com',
          image: r.image || ''
        });
      });
    }

    // Capture acquisition signal for first-touch attribution.
    // 2026-05-16: always send landing_page + referrer regardless of UTM presence
    // so organic / direct traffic still records the entry point. Pre-fix, the
    // acquisition object was null when no UTM params existed, leaving the
    // worker's extractor with only the Referer header (typically the same-origin
    // root) and an empty landingPage. UTM fields stay empty strings for organic
    // traffic; downstream safeStr/safeUrl tolerate them.
    const utmParams = new URLSearchParams(window.location.search);
    const utmSource = utmParams.get('utm_source') || '';
    const utmMedium = utmParams.get('utm_medium') || '';
    const utmCampaign = utmParams.get('utm_campaign') || '';
    const utmContent = utmParams.get('utm_content') || '';
    const utmTerm = utmParams.get('utm_term') || '';
    const acquisitionData = {
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      utm_term: utmTerm,
      // window.location.href (full URL) is required: worker's safeUrl() enforces
      // ^https?:// and drops paths-only to empty string. Reviewer catch 2026-05-16.
      landing_page: window.location.href,
      referrer: document.referrer || '',
    };

    const submissionData = {
      email: state.answers.email,
      name: state.answers.firstName || '',
      shopping_for: state.answers.intent || '',
      atmosphere: state.answers.atmosphere || '',
      time: state.answers.time || '',
      place: state.answers.place || '',
      impression: state.answers.impression || '',
      avoid: state.answers.avoid ? state.answers.avoid.join(', ') : '',
      approach: '',
      recommendations: allRecs.length > 0 ? JSON.stringify(allRecs) : '',
      // Archetype data from quiz scoring
      archetype: state.recommendations?.archetype || '',
      archetype_name: state.recommendations?.profile?.name || '',
      archetype_scene: state.recommendations?.profile?.scene || '',
      quiz_currency: (function() { try { return localStorage.getItem('baCurrency') || localStorage.getItem('bacurr_currency') || ''; } catch(e) { return ''; } })(),
      visitor_id: (function() { try { return localStorage.getItem('pc_visitor_id') || (document.cookie.match(/pc_vid=([a-zA-Z0-9_-]+)/) || [])[1] || ''; } catch(e) { return ''; } })(),
      // 2026-05-16: phone + SMS consent now flow to worker for Shopify metafield
      // writes. Pre-fix these only reached Klaviyo (profileData), leaving Shopify
      // customer record without phone for quiz-only signups (no SMS/WhatsApp
      // marketing eligibility, no CRM phone display). Worker quiz.js:206-224 has
      // had the storage path for months — was dead code until this addition.
      quiz_phone: state.answers.phone || '',
      quiz_sms_consent: !!state.answers.smsConsent
    };
    submissionData.acquisition = acquisitionData;

    // The results render without waiting for this save, so a shopper who taps
    // a card within a few seconds navigates away mid-request. keepalive lets
    // the request outlive the page (MDN RequestInit: "the browser will not
    // abort the associated request if the page that initiated it is
    // unloaded"; body limit 64 KiB). One retry covers a dropped connection,
    // a 429 or a 5xx; a 4xx is a real rejection and is not retried.
    const body = JSON.stringify(submissionData);
    // keepalive shares a 64 KiB in-flight budget and some older Chrome builds
    // refuse it on a preflighted (X-API-Key) request, so only the first
    // attempt uses it and the retry is a plain fetch.
    const smallEnough = new Blob([body]).size < 60000;
    const post = (keepalive) => fetch(CONFIG.submissionWorkerUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': CONFIG.storefrontApiKey
      },
      body,
      keepalive
    });

    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const response = await post(attempt === 1 && smallEnough);
        const retryable = response.status === 429 || response.status >= 500;
        if (retryable && attempt === 1) {
          await new Promise(r => setTimeout(r, 1500));
          continue;
        }
        if (!response.ok) {
          console.error('[Quiz] Shopify submission rejected:', response.status);
          return null;
        }
        const result = await response.json();
        if (result.success) {
          return result;
        }
        console.error('[Quiz] Shopify submission error:', response.status, result.error);
        return null;
      } catch (error) {
        console.error('[Quiz] Failed to send to Shopify (attempt ' + attempt + '):', error);
        if (attempt === 1) {
          await new Promise(r => setTimeout(r, 1500));
          continue;
        }
        return null;
      }
    }
    return null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PRODUCT DATA LOADING
  // Loads real product data from Liquid-generated JSON
  // ─────────────────────────────────────────────────────────────────────────

  let productData = null;

  function loadProductData() {
    if (productData) return productData;

    const dataElement = document.getElementById('quiz-products-data');
    if (dataElement) {
      try {
        const rawData = JSON.parse(dataElement.textContent);

        // Check if using new multi-collection format
        if (rawData.rawProducts) {
          // Merge and deduplicate products from multiple collections
          const seenIds = new Set();
          const allProducts = [];

          // Combine all collection arrays (mens, womens, unisex)
          const collections = ['mens', 'womens', 'unisex'];
          collections.forEach(collection => {
            const products = rawData.rawProducts[collection] || [];
            products.forEach(product => {
              if (product && product.id && !seenIds.has(product.id)) {
                seenIds.add(product.id);
                allProducts.push(product);
              }
            });
          });

          productData = {
            products: allProducts,
            currency: rawData.currency || 'AED',
            totalProducts: rawData.totalProducts || allProducts.length
          };
        } else {
          // Legacy format with products array
          productData = rawData;
        }
      } catch (e) {
        console.error('[Quiz] Failed to parse product data:', e);
        productData = { products: [], currency: 'AED', totalProducts: 0 };
      }
    } else {
      console.warn('[Quiz] Product data element not found');
      productData = { products: [], currency: 'AED', totalProducts: 0 };
    }
    return productData;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ARCHETYPE CALCULATION
  // Scores archetypes based on quiz answers using SCORING_MAP
  // ─────────────────────────────────────────────────────────────────────────

  function calculateArchetype() {
    const scores = { sovereign: 0, rebel: 0, philosopher: 0, seducer: 0, explorer: 0, ascetic: 0 };

    // Q4: Place (5/2-3 pts) - PRIMARY
    if (state.answers.place && SCORING_MAP.place[state.answers.place]) {
      const m = SCORING_MAP.place[state.answers.place];
      scores[m.primary.archetype] += m.primary.pts;
      scores[m.secondary.archetype] += m.secondary.pts;
    }

    // Q5: Impression (4/1-2 pts)
    if (state.answers.impression && SCORING_MAP.impression[state.answers.impression]) {
      const m = SCORING_MAP.impression[state.answers.impression];
      scores[m.primary.archetype] += m.primary.pts;
      scores[m.secondary.archetype] += m.secondary.pts;
    }

    // Q2: Atmosphere (3/1 pts)
    if (state.answers.atmosphere && SCORING_MAP.atmosphere[state.answers.atmosphere]) {
      const m = SCORING_MAP.atmosphere[state.answers.atmosphere];
      scores[m.primary.archetype] += m.primary.pts;
      scores[m.secondary.archetype] += m.secondary.pts;
    }

    // Q3: Time (2/1 pts)
    if (state.answers.time && SCORING_MAP.time[state.answers.time]) {
      const m = SCORING_MAP.time[state.answers.time];
      scores[m.primary.archetype] += m.primary.pts;
      scores[m.secondary.archetype] += m.secondary.pts;
    }

    // Q6: Avoid penalties (-2 each)
    if (state.answers.avoid && state.answers.avoid.length > 0) {
      state.answers.avoid.forEach(key => {
        if (SCORING_MAP.avoid[key]?.penalize) {
          scores[SCORING_MAP.avoid[key].penalize] += SCORING_MAP.avoid[key].pts;
        }
      });
    }

    // Find winner using tiebreak order
    let winner = TIEBREAK_ORDER[0];
    let highest = -Infinity;
    TIEBREAK_ORDER.forEach(arch => {
      if (scores[arch] > highest) {
        highest = scores[arch];
        winner = arch;
      }
    });

    // Calculate match confidence: winner's share of total positive points
    var totalPositive = 0;
    Object.values(scores).forEach(function(s) { if (s > 0) totalPositive += s; });
    var rawConfidence = totalPositive > 0 ? highest / totalPositive : 0.5;
    // Scale to 78-98% range for customer-friendly display
    var matchPercent = Math.round(78 + rawConfidence * 20);
    if (matchPercent > 98) matchPercent = 98;

    // Store for product cards
    state._matchScore = matchPercent;

    return winner;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PRODUCT RECOMMENDATIONS
  // Uses archetype-based fixed product lists
  // ─────────────────────────────────────────────────────────────────────────

  async function generateRecommendations() {
    // Load product data from Liquid JSON
    const data = loadProductData();

    // Calculate winning archetype from quiz answers
    const archetype = calculateArchetype();
    const profile = ARCHETYPES[archetype];
    const productList = ARCHETYPE_PRODUCTS[archetype] || [];

    // Build lookup by handle from preloaded Liquid data
    const productMap = new Map();
    if (data.products) {
      data.products.forEach(p => p.handle && productMap.set(p.handle, p));
    }

    // Find handles missing from preloaded data (Liquid caps at 50 per collection)
    const missingHandles = productList
      .filter(item => !productMap.has(item.handle))
      .map(item => item.handle);

    // Fetch missing products via Shopify AJAX API
    if (missingHandles.length > 0) {
      const fetches = missingHandles.map(handle =>
        fetch(`/products/${handle}.js`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      );
      const results = await Promise.all(fetches);
      results.forEach(p => {
        // /products/<handle>.js still serves hidden (unlisted) products; the
        // Liquid preload already skips them, so skip them here too.
        const isHidden = p && Array.isArray(p.tags) && p.tags.some(t => String(t).toLowerCase() === 'hidden');
        if (p && p.handle && !isHidden) {
          productMap.set(p.handle, {
            id: p.id,
            title: p.title,
            handle: p.handle,
            vendor: p.vendor,
            price: p.price ? (p.price / 100).toLocaleString('en-US', { style: 'currency', currency: p.currency || 'AED' }) : '',
            priceRaw: p.price || 0,
            image: p.featured_image || (p.images && p.images[0]) || '',
            url: `/products/${p.handle}`,
            variantId: p.variants && p.variants[0] ? p.variants[0].id : null,
            available: p.available
          });
        }
      });
    }

    // Categorize products by their ORIGINAL position in archetype list
    // Positions 1-3 = Primary, 4-6 = Seasonal, 7-9 = Discovery
    const primary = [];
    const seasonal = [];
    const discovery = [];
    const missing = [];

    productList.forEach((item, index) => {
      const product = productMap.get(item.handle);
      // Never recommend a pick nobody can buy (operator + Firas, 2026-09-30):
      // sold-out and hidden picks drop out until they are restocked or replaced.
      if (product && product.available !== false) {
        // Use static notes from ARCHETYPE_PRODUCTS as fallback (AJAX-fetched products lack metafields)
        if (!product.notes && item.notes) {
          product.notes = item.notes;
        }
        var category = index < 3 ? 'primary' : index < 6 ? 'seasonal' : 'discovery';
        const formatted = formatProductForDisplay(product, category);
        if (index < 3) {
          primary.push(formatted);
        } else if (index < 6) {
          seasonal.push(formatted);
        } else {
          discovery.push(formatted);
        }
      } else {
        missing.push(item);
      }
    });

    // Log missing handles for debugging
    if (missing.length > 0) {
      console.warn('[Quiz] Missing handles:', missing.map(m => m.name));
    }

    // If no products found, log warning
    if (primary.length === 0 && seasonal.length === 0 && discovery.length === 0) {
      console.warn('[Quiz] No products found for archetype:', archetype, '- handles may need to be populated');
    }

    return {
      profile: profile,
      archetype: archetype,
      categories: {
        primary: primary,
        seasonal: seasonal,
        discovery: discovery
      }
    };
  }

  function formatProductForDisplay(product, category) {
    var rawNotes = product.notes || product.shortDescription || '';
    // Condense multi-line "Top: ...\n\nMiddle: ...\n\nBase: ..." into "top · middle · base"
    if (rawNotes.indexOf('Top:') === 0 || rawNotes.indexOf('\nTop:') !== -1) {
      rawNotes = rawNotes
        .replace(/Top:\s*/g, '')
        .replace(/Middle:\s*/g, '')
        .replace(/Base:\s*/g, '')
        .replace(/\n{2,}/g, ' \u00b7 ')
        .replace(/\n/g, ' ')
        .trim();
    }
    return {
      id: product.id,
      variantId: product.variantId,
      brand: product.vendor || 'Unknown',
      name: product.title || 'Unnamed',
      notes: rawNotes,
      price: product.priceRaw ? product.priceRaw / 100 : 0,
      priceFormatted: product.price || '',
      image: product.image || '/placeholder-bottle.jpg',
      url: product.url || '#',
      matchScore: (function() {
        var base = state._matchScore || 88;
        var offset = category === 'primary' ? 0 : category === 'seasonal' ? -5 : -12;
        // No random jitter: the same answers must show the same numbers on a retake.
        return Math.min(98, Math.max(72, base + offset));
      })()
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RESULTS DISPLAY
  // ─────────────────────────────────────────────────────────────────────────

  async function showResults() {

    // Clear saved progress since quiz is complete
    clearProgress();

    // Hide loading
    if (elements.loading) {
      elements.loading.style.display = 'none';
      elements.loading.setAttribute('data-visible', 'false');
    } else {
      console.warn('[Quiz] Loading element not found');
    }

    // Update profile display
    if (elements.profileName && state.recommendations?.profile) {
      elements.profileName.textContent = state.recommendations.profile.name;
    }

    // Update scene line (new archetype system)
    const sceneEl = document.querySelector('[data-profile-scene]');
    if (sceneEl && state.recommendations?.profile?.scene) {
      sceneEl.textContent = state.recommendations.profile.scene;
    }

    if (elements.profileDescription && state.recommendations?.profile) {
      elements.profileDescription.textContent = state.recommendations.profile.description;
    }

    // Render product cards
    renderProductCards('primary', state.recommendations?.categories?.primary || []);
    renderProductCards('seasonal', state.recommendations?.categories?.seasonal || []);
    renderProductCards('discovery', state.recommendations?.categories?.discovery || []);

    // Show results section
    if (elements.results) {
      elements.results.style.display = 'block';
      elements.results.setAttribute('data-visible', 'true');
    } else {
      console.error('[Quiz] Results element not found!');
    }

    // Hide original archetype card (compact variant shows inside results)
    if (elements.archetypeLink) {
      elements.archetypeLink.style.display = 'none';
    }

    // Show discount tracker immediately with results
    if (elements.discountTracker) {
      elements.discountTracker.setAttribute('data-visible', 'true');
    }
    updateCartSummary();

    // Trigger Nova Multi-Currency to convert dynamically rendered prices
    if (window.baCurr && typeof window.baCurr.rePeat === 'function') {
      window.baCurr.rePeat();
    }
  }

  function renderProductCards(category, products) {
    const container = document.querySelector(`[data-category="${category}"]`);
    if (!container || !elements.productTemplate) return;

    container.innerHTML = '';
    // Hide a whole category (heading included) when all its picks dropped out.
    const section = container.closest('.quiz-category');
    if (section) section.style.display = products.length === 0 ? 'none' : '';

    products.forEach((product, index) => {
      const card = elements.productTemplate.content.cloneNode(true);

      // Fill in product data
      const img = card.querySelector('[data-image]');
      if (img) {
        img.src = product.image;
        img.alt = `${product.brand} ${product.name}`;
      }

      const badge = card.querySelector('[data-badge]');
      if (badge) badge.textContent = `${product.matchScore}% Match`;

      const brand = card.querySelector('[data-brand]');
      if (brand) brand.textContent = product.brand;

      const name = card.querySelector('[data-name]');
      if (name) name.textContent = product.name;

      const notes = card.querySelector('[data-notes]');
      if (notes) notes.textContent = product.notes;

      const price = card.querySelector('[data-price]');
      if (price) price.innerHTML = formatCurrency(product.price);

      const addToCart = card.querySelector('[data-action="add-to-cart"]');
      if (addToCart) {
        addToCart.setAttribute('data-variant-id', product.variantId);
        addToCart.setAttribute('data-price', product.price);
        addToCart.setAttribute('data-product-id', product.id);
      }

      // Add animation delay
      const article = card.querySelector('.quiz-product-card');
      if (article) {
        article.style.animationDelay = `${index * CONFIG.staggerDelay}ms`;
      }

      container.appendChild(card);
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // QUANTITY SELECTORS
  // ─────────────────────────────────────────────────────────────────────────

  function bindQuantitySelectors() {
    document.addEventListener('click', (e) => {
      // Toggle dropdown
      const trigger = e.target.closest('.quiz-quantity-select__trigger');
      if (trigger) {
        const select = trigger.closest('.quiz-quantity-select');
        const isOpen = select.getAttribute('data-open') === 'true';

        // Close all other dropdowns
        document.querySelectorAll('.quiz-quantity-select').forEach(s => {
          s.setAttribute('data-open', 'false');
        });

        // Toggle this one
        select.setAttribute('data-open', (!isOpen).toString());
        return;
      }

      // Select option
      const option = e.target.closest('.quiz-quantity-select__option');
      if (option) {
        const select = option.closest('.quiz-quantity-select');
        const qty = parseInt(option.getAttribute('data-qty'), 10);
        const isBundle = option.hasAttribute('data-bundle');

        // Update display
        const display = select.querySelector('[data-qty-display]');
        if (display) {
          display.textContent = isBundle ? '3-Pack' : qty;
        }

        // Update selected state
        select.querySelectorAll('.quiz-quantity-select__option').forEach(opt => {
          opt.setAttribute('data-selected', 'false');
        });
        option.setAttribute('data-selected', 'true');

        // Close dropdown
        select.setAttribute('data-open', 'false');

        // Store quantity on the card
        const card = select.closest('.quiz-product-card');
        if (card) {
          card.setAttribute('data-quantity', qty);
          card.setAttribute('data-is-bundle', isBundle ? 'true' : 'false');
        }

        return;
      }

      // Add to cart
      const addBtn = e.target.closest('[data-action="add-to-cart"]');
      if (addBtn) {
        addToCart(addBtn);
        return;
      }

      // Close dropdowns when clicking outside
      if (!e.target.closest('.quiz-quantity-select')) {
        document.querySelectorAll('.quiz-quantity-select').forEach(s => {
          s.setAttribute('data-open', 'false');
        });
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CART MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────

  async function addToCart(button) {
    const variantId = button.getAttribute('data-variant-id');
    const price = parseFloat(button.getAttribute('data-price'));
    const productId = button.getAttribute('data-product-id');

    const card = button.closest('.quiz-product-card');
    const quantity = parseInt(card?.getAttribute('data-quantity') || '1', 10);
    const isBundle = card?.getAttribute('data-is-bundle') === 'true';

    // Calculate price (10% off for bundles — display only, real discount applied via code)
    const itemPrice = isBundle ? price * 0.9 : price;
    const totalPrice = itemPrice * quantity;

    const cartItem = { variantId, productId, quantity, price: itemPrice, total: totalPrice, isBundle };
    function markUnavailable(label) {
      button.textContent = label;
      button.disabled = true;
      button.style.background = 'rgba(200, 100, 100, 0.3)';
    }

    let doneLabel = 'Added ✓';
    button.textContent = 'Adding...';
    button.disabled = true;
    button.style.background = 'rgba(230, 196, 127, 0.3)';

    // Add to Shopify cart (if in Shopify environment). Only an add Shopify
    // accepted counts toward the cart summary, the 3-item 10% goal and the
    // discount code: /cart/add.js answers 422 for a sold-out variant.
    if (window.Shopify) {
      if (!variantId) {
        console.error('[Quiz] Cannot add to cart: no variant ID for product', productId);
        markUnavailable('Unavailable');
        return;
      }
      const numericId = String(variantId).replace('gid://shopify/ProductVariant/', '');
      // Shopify can add part of a multi-bottle quantity and still answer 422
      // ("You can't add more"), so for quantity > 1 count what actually landed.
      const cartQtyOf = async () => {
        const cart = await fetch('/cart.js').then(r => r.json());
        return cart.items.filter(i => String(i.variant_id) === numericId).reduce((sum, i) => sum + i.quantity, 0);
      };
      let landed = 0;
      let refusedStatus = 0;
      try {
        const before = quantity > 1 ? await cartQtyOf() : 0;
        const addResp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: numericId,
              quantity: quantity
            }]
          })
        });
        if (addResp.ok) {
          landed = quantity;
        } else {
          refusedStatus = addResp.status;
          if (quantity > 1 && refusedStatus === 422) landed = Math.max(0, (await cartQtyOf()) - before);
        }
      } catch (error) {
        console.error('Error adding to Shopify cart:', error);
        markUnavailable('Try again');
        button.disabled = false;
        return;
      }
      if (landed === 0) {
        console.warn('[Quiz] /cart/add.js refused variant', numericId, 'status', refusedStatus);
        markUnavailable(refusedStatus === 422 ? 'Sold out' : 'Unavailable');
        return;
      }
      if (landed < quantity) {
        cartItem.quantity = landed;
        cartItem.total = cartItem.price * landed;
        doneLabel = 'Added ' + landed + ' (limited stock)';
      }
      state.cart.items.push(cartItem);
      updateCartSummary();

      // Check if total items >= 3 and no discount code yet — generate one
      const totalQty = state.cart.items.reduce((sum, item) => sum + item.quantity, 0);
      if (totalQty >= 3 && !state.cart.discountCode && CONFIG.submissionWorkerUrl) {
        try {
          const discountResp = await fetch(CONFIG.submissionWorkerUrl + '/discount', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-API-Key': CONFIG.storefrontApiKey },
            body: JSON.stringify({ action: 'generate' })
          });
          const discountData = await discountResp.json();
          if (discountData.success && discountData.code) {
            state.cart.discountCode = discountData.code;
            // Apply discount code to Shopify cart via /cart/update.js
            try {
              const applyResp = await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ discount: discountData.code })
              });
              const cartData = await applyResp.json();
            } catch (applyErr) {
              console.warn('[Quiz] /cart/update.js discount apply failed, will use redirect at checkout:', applyErr);
            }
            updateDiscountTracker();
          }
        } catch (discountErr) {
          console.error('[Quiz] Discount code generation failed:', discountErr);
        }
      }
    } else {
      state.cart.items.push(cartItem);
      updateCartSummary();
    }

    button.textContent = doneLabel;

    // Track in Klaviyo
    if (window._learnq) {
      window._learnq.push(['track', 'Quiz Add to Cart', {
        product_id: productId,
        variant_id: variantId,
        quantity: quantity,
        price: totalPrice,
        is_bundle: isBundle
      }]);
    }
  }

  function updateCartSummary() {
    const itemCount = state.cart.items.reduce((sum, item) => sum + item.quantity, 0);
    const total = state.cart.items.reduce((sum, item) => sum + item.total, 0);

    state.cart.total = total;

    if (elements.cartCount) {
      elements.cartCount.textContent = itemCount;
    }

    if (elements.cartTotal) {
      elements.cartTotal.innerHTML = formatCurrency(total);
    }

    // Show/hide summary bar
    if (elements.resultsSummary) {
      elements.resultsSummary.setAttribute('data-visible', (itemCount > 0).toString());
    }

    // Update discount progress tracker
    updateDiscountTracker();

    // Trigger Nova Multi-Currency to convert dynamically updated prices
    if (window.baCurr && typeof window.baCurr.rePeat === 'function') {
      window.baCurr.rePeat();
    }
  }

  function updateDiscountTracker() {
    if (!elements.discountTracker) return;

    var totalQty = state.cart.items.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    var isResultsVisible = elements.results && elements.results.getAttribute('data-visible') === 'true';

    // Show/hide tracker
    elements.discountTracker.setAttribute('data-visible', isResultsVisible ? 'true' : 'false');
    elements.discountTracker.setAttribute('data-count', Math.min(totalQty, 3));

    // Progress bar fill — set background-size on the bar track itself (no child element)
    var barTrack = elements.discountTracker.querySelector('.quiz-discount-tracker__bar');
    if (barTrack) {
      var pct = Math.round(Math.min(totalQty / 3, 1) * 100);
      void barTrack.offsetHeight;
      barTrack.style.backgroundSize = pct + '% 100%';
    }

    // Update milestone diamonds
    var milestones = elements.discountTracker.querySelectorAll('.quiz-discount-tracker__milestone');
    milestones.forEach(function (ms) {
      var msNum = parseInt(ms.getAttribute('data-ms'), 10);
      ms.setAttribute('data-reached', (totalQty >= msNum).toString());
    });

    // Update count badge
    var countEl = elements.discountTracker.querySelector('[data-discount-count]');
    if (countEl) {
      countEl.textContent = Math.min(totalQty, 3) + ' / 3';
    }

    // Update message
    if (elements.discountMessage) {
      if (totalQty === 0) {
        elements.discountMessage.innerHTML = 'Add 3 fragrances to unlock <strong>10% off</strong>';
      } else if (totalQty === 1) {
        elements.discountMessage.innerHTML = '2 more for <strong>10% off</strong> your collection';
      } else if (totalQty === 2) {
        elements.discountMessage.innerHTML = 'Just 1 more to unlock <strong>10% off!</strong>';
      } else if (state.cart.discountCode) {
        elements.discountMessage.innerHTML = '<strong>10% off unlocked!</strong> Code: <strong>' + state.cart.discountCode + '</strong>';
      } else {
        elements.discountMessage.innerHTML = '<strong>10% discount unlocked!</strong>';
      }
    }

    // Celebration state
    elements.discountTracker.setAttribute('data-unlocked', (totalQty >= 3).toString());

    // Update discount status in summary bar
    if (elements.discountStatus) {
      if (totalQty >= 3 && state.cart.discountCode) {
        elements.discountStatus.textContent = '10% OFF applied';
        elements.discountStatus.setAttribute('data-active', 'true');
      } else if (totalQty >= 3) {
        elements.discountStatus.textContent = '10% OFF';
        elements.discountStatus.setAttribute('data-active', 'true');
      } else if (totalQty > 0) {
        var remaining = 3 - totalQty;
        elements.discountStatus.textContent = 'Add ' + remaining + ' more for 10% off';
        elements.discountStatus.setAttribute('data-active', 'false');
      } else {
        elements.discountStatus.textContent = '';
      }
    }
  }

  async function proceedToCheckout() {
    // Track checkout initiation
    if (window._learnq) {
      window._learnq.push(['track', 'Quiz Checkout Started', {
        item_count: state.cart.items.length,
        total: state.cart.total
      }]);
    }

    if (state.cart.discountCode) {
      // Re-apply discount to cart right before navigating (belt and suspenders)
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: state.cart.discountCode })
        });
      } catch (e) { }
      // Redirect through /discount/CODE which guarantees the session cookie is set
      window.location.href = '/discount/' + encodeURIComponent(state.cart.discountCode) + '?redirect=/cart';
    } else {
      window.location.href = '/cart';
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RESULTS ACTIONS
  // ─────────────────────────────────────────────────────────────────────────

  function bindResultsActions() {
    // Actions are handled in bindNavigationButtons
  }

  function retakeQuiz() {
    // Reset state (1-indexed questions, 7 total)
    state.currentStep = 1;
    state.answers = {
      intent: null,       // Q1
      atmosphere: null,   // Q2
      time: null,         // Q3
      place: null,        // Q4
      impression: null,   // Q5 (What should stay in the room)
      avoid: [],          // Q6 (multi-select)
      email: '',          // Q7
      firstName: '',      // Q7
      smsConsent: false,  // Q7
      phone: ''           // Q7
    };
    state.cart = { items: [], total: 0, discountCode: null };
    state.recommendations = null;

    // Reset UI
    document.querySelectorAll('.quiz-option').forEach(opt => {
      opt.setAttribute('data-selected', 'false');
    });
    document.querySelectorAll('.quiz-multi-option').forEach(opt => {
      opt.setAttribute('data-selected', 'false');
    });

    // Hide results
    if (elements.results) {
      elements.results.style.display = 'none';
      elements.results.setAttribute('data-visible', 'false');
    }

    // Restore original archetype card
    if (elements.archetypeLink) {
      elements.archetypeLink.style.display = '';
    }
    if (elements.resultsSummary) {
      elements.resultsSummary.setAttribute('data-visible', 'false');
    }
    if (elements.discountTracker) {
      elements.discountTracker.setAttribute('data-visible', 'false');
      elements.discountTracker.setAttribute('data-unlocked', 'false');
    }

    // Show first question (Q1: Intent)
    showQuestion(1);
  }

  function shareResults() {
    const profile = state.recommendations?.profile;
    const shareText = `I discovered my scent profile: ${profile?.name || 'Explorer'}! Take the Parfum Central quiz to find yours.`;
    const shareUrl = window.location.href;

    if (navigator.share) {
      navigator.share({
        title: 'My Scent Profile - Parfum Central',
        text: shareText,
        url: shareUrl
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => {
        alert('Link copied to clipboard!');
      }).catch(console.error);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // INITIALIZE ON DOM READY
  // ─────────────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.ParfumQuiz = {
    state,
    CONFIG,
    ARCHETYPES,
    SCORING_MAP,
    ARCHETYPE_PRODUCTS,
    calculateArchetype
  };

})();
